import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Spinner } from "react-bootstrap";
import Select from "../../Common/FormElements/Select/Select";
import TextArea from "../../Common/FormElements/TextArea/TextArea";
import Button from "../../Common/FormElements/Button/Button";
import { formatEuro } from "./QuotePill";
import { cancelSale, fetchSaleCancelPreview } from "../../../REDUX/actions/appProjects";
import { closeCenteredModal } from "../../../REDUX/actions/centeredModal";

/**
 * Quote ⋮ "Annuler la commande": the customer withdraws (C10). What she paid
 * is to be refunded by the store — no credit note, it is between the store
 * and its customer. The units stay on the store's cart (its own Poseidon
 * order); the quote goes back to "Envoyé".
 */
const CancelSaleModal = ({ saleId, projectId, onCancelled }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    dispatch(fetchSaleCancelPreview(saleId, controller.signal)).then((data) => {
      if (!data) return;
      setPreview(data);
      setReason({ name: data.reasons[0]?.label, code: data.reasons[0]?.code });
    });
    return () => controller.abort();
  }, [dispatch, saleId]);

  const close = () => dispatch(closeCenteredModal());
  const submit = async () => {
    setSaving(true);
    const data = await dispatch(
      cancelSale(saleId, projectId, { cancellationReason: reason?.code, note }, t),
    );
    setSaving(false);
    if (!data) return;
    if (onCancelled) onCancelled(data);
    close();
  };

  return (
    <div className="p-4 bg-white br10" style={{ width: 560, maxWidth: "90vw" }}>
      <h3 className="text-blue bold f18 quickSand mb-3">
        {t("Cancel the order")}
        {preview?.orderNumber ? ` n°${preview.orderNumber}` : ""}
      </h3>
      {!preview ? (
        <div className="d-flex justify-content-center py-4">
          <Spinner />
        </div>
      ) : (
        <>
          <p className="f14 sBold mb-2">{t("Cancelling the order means:")}</p>
          <ul className="f14 ps-3 mb-3">
            {preview.paid > 0 && (
              <li>
                {t(
                  "The {{amount}} received (last on {{date}}) is to be refunded to {{customer}} by the store.",
                  {
                    amount: formatEuro(preview.paid),
                    date: preview.paidOn ? moment(preview.paidOn).format("DD/MM") : "-",
                    customer: preview.customerName ?? "-",
                  },
                )}
              </li>
            )}
            {preview.quote && (
              <li>
                {t(
                  "The quote no. {{number}} goes back to “Sent” and stays editable in project No. {{project}}.",
                  { number: preview.quote.id, project: preview.project?.number ?? "-" },
                )}
              </li>
            )}
            <li>
              {t(
                "The units stay on your order to Poseidon: delete or edit it in Orders if needed.",
              )}
            </li>
          </ul>
          <p className="f14 sBold mb-1">{t("Reason for cancellation")}</p>
          <Select
            appendToSelf
            options={preview.reasons.map(({ code, label }) => ({ name: label, code }))}
            value={reason}
            callback={(e) => setReason(e.value)}
            className="mb-3"
          />
          <TextArea
            placeholder={t("Comment")}
            value={note}
            callback={(e) => setNote(e.target.value)}
          />
          <div className="d-flex justify-content-end gap-3 mt-4">
            <Button variant="outline" onClick={close}>
              {t("Back")}
            </Button>
            <Button
              loading={saving}
              disabled={!reason}
              onClick={saving || !reason ? undefined : submit}
            >
              {t("Cancel the order")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CancelSaleModal;
